import { createClient } from "@supabase/supabase-js";
import type {
  AnalysisJobType,
  AnalysisSelectionMode,
  CreditCancellationType,
  CreditEvidenceAssetType,
  CreditEvidenceScope,
  Database,
} from "./types";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy .env.example to .env.local."
  );
}

export const supabase = createClient<Database>(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * Synchronous access token for MapLibre's `transformRequest`, which cannot
 * await. Kept fresh by the auth listener (fires on sign-in/refresh/sign-out)
 * plus an initial getSession() for the cold-start session restore.
 *
 * The previous approach read `(supabase.auth as any).currentSession` — that
 * property does NOT exist in auth-js v2 (sessions live in storage, accessed
 * via async getSession()), so it was always undefined and tile requests went
 * out with NO Authorization header: every SECURITY INVOKER tile layer
 * (leases_owned / polling_points / css_areas) rendered empty under RLS.
 */
let currentAccessToken: string | null = null;
void supabase.auth.getSession().then(({ data }) => {
  currentAccessToken = data.session?.access_token ?? currentAccessToken;
});
supabase.auth.onAuthStateChange((_event, session) => {
  currentAccessToken = session?.access_token ?? null;
});
export function getAccessTokenSync(): string | null {
  return currentAccessToken;
}

/**
 * Functions URL for the `tiles` Edge Function. Used by MapView when building
 * vector source tile URLs.
 */
export const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;

/**
 * Typed wrappers for the access-management RPCs in
 * `finalized_schema_and_plan/06_access_management.sql`. Add new RPCs here
 * rather than calling `supabase.rpc(...)` from components.
 */
export const rpc = {
  appRole: () => supabase.rpc("app_role"),

  buyerStorefront: () => supabase.rpc("buyer_storefront"),

  staffCreditDashboard: (vintageYear: number) =>
    supabase.rpc("staff_credit_dashboard", { p_vintage_year: vintageYear }),

  staffCreditDashboardByVintage: (vintageYear: number | null) =>
    supabase.rpc("staff_credit_dashboard_by_vintage", { p_vintage_year: vintageYear }),

  staffRecentCreditEvents: (vintageYear: number | null, limit = 10) =>
    supabase.rpc("staff_recent_credit_events", { p_vintage_year: vintageYear, p_limit: limit }),

  // Cancel issued credits for one lease-year. Sold units (transferred or
  // retired) are never touched and come back in skipped_sold. Records the
  // reason and the acting user as a permanent cancellation entry.
  cancelCreditsForLeaseYear: (args: {
    leaseNumber: string;
    vintageYear: number;
    cancellationType: CreditCancellationType;
    reasonCode: string;
    reasonNote?: string | null;
    maxCount?: number | null;
  }) => supabase.rpc("cancel_carbon_credits_for_lease_year", {
    p_lease_number: args.leaseNumber,
    p_vintage_year: args.vintageYear,
    p_cancellation_type: args.cancellationType,
    p_reason_code: args.reasonCode,
    p_reason_note: args.reasonNote ?? null,
    p_max_count: args.maxCount ?? null,
  }),

  staffFieldActivity: (since: string | null = null, until: string | null = null) =>
    supabase.rpc("staff_field_activity", { p_since: since, p_until: until }),

  // Feature-level drill-down behind staff_field_activity. Every filter is
  // optional and null means "no filter", which is also the RPC's own default.
  staffFieldFeatures: (args: {
    since?: string | null;
    until?: string | null;
    collector?: string | null;
    lease?: string | null;
    sourceTable?: string | null;
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    supabase.rpc("staff_field_features", {
      p_since: args.since ?? null,
      p_until: args.until ?? null,
      p_collector: args.collector ?? null,
      p_lease: args.lease ?? null,
      p_source_table: args.sourceTable ?? null,
      p_include_archived: args.includeArchived ?? false,
      p_limit: args.limit ?? 500,
      p_offset: args.offset ?? 0,
    }),

  buyerEvidenceLibrary: () => supabase.rpc("buyer_evidence_library"),

  buyerCreditEvidence: (creditId: string) =>
    supabase.rpc("buyer_credit_evidence", { p_credit_id: creditId }),

  buyerCreditMapPoints: (limit = 250) =>
    supabase.rpc("buyer_credit_map_points", { p_limit: limit }),

  updateMyProfile: (args: { firstName: string; lastName: string; phone?: string | null }) =>
    supabase.rpc("update_my_profile", {
      p_first_name: args.firstName,
      p_last_name: args.lastName,
      p_phone: args.phone ?? null,
    }),

  staffCreditEvidenceAssets: () => supabase.rpc("staff_credit_evidence_assets"),

  staffCreateCreditEvidenceAsset: (args: {
    scopeType: CreditEvidenceScope;
    vintageYear: number | null;
    leaseNumber: string | null;
    assetType: CreditEvidenceAssetType;
    title: string;
    description: string | null;
    storageBucket: "credit-evidence" | "attachments" | null;
    storagePath: string | null;
    externalUrl: string | null;
    fileName: string | null;
    mimeType: string | null;
    fileSize: number | null;
    capturedAt: string | null;
    sourceType: string | null;
    sourceId: string | null;
    isPublished: boolean;
    displayOrder?: number;
  }) => supabase.rpc("staff_create_credit_evidence_asset", {
    p_scope_type: args.scopeType,
    p_vintage_year: args.vintageYear,
    p_lease_number: args.leaseNumber,
    p_asset_type: args.assetType,
    p_title: args.title,
    p_description: args.description,
    p_storage_bucket: args.storageBucket,
    p_storage_path: args.storagePath,
    p_external_url: args.externalUrl,
    p_file_name: args.fileName,
    p_mime_type: args.mimeType,
    p_file_size: args.fileSize,
    p_captured_at: args.capturedAt,
    p_source_type: args.sourceType,
    p_source_id: args.sourceId,
    p_is_published: args.isPublished,
    p_display_order: args.displayOrder ?? 0,
  }),

  staffSetCreditEvidencePublished: (assetId: string, isPublished: boolean) =>
    supabase.rpc("staff_set_credit_evidence_published", {
      p_asset_id: assetId,
      p_is_published: isPublished,
    }),

  staffArchiveCreditEvidenceAsset: (assetId: string) =>
    supabase.rpc("staff_archive_credit_evidence_asset", { p_asset_id: assetId }),

  retireBuyerCredits: (args: { creditIds: string[]; beneficiary: string; purpose?: string | null }) =>
    supabase.rpc("retire_buyer_credits", {
      p_credit_ids: args.creditIds,
      p_beneficiary: args.beneficiary,
      p_purpose: args.purpose ?? null,
    }),

  userCanAccessLease: (leaseNumber: string) =>
    supabase.rpc("user_can_access_lease", { p_lease_number: leaseNumber }),

  currentCssTier: (lon: number, lat: number, year: number) =>
    supabase.rpc("current_css_tier", { p_lon: lon, p_lat: lat, p_year: year }),

  enqueueAnalysisJob: (args: {
    jobType: AnalysisJobType;
    selectionMode: AnalysisSelectionMode;
    analysisYear: number | null;
    leaseNumbers?: string[] | null;
  }) =>
    supabase.rpc("enqueue_analysis_job", {
      p_job_type: args.jobType,
      p_selection_mode: args.selectionMode,
      p_analysis_year: args.analysisYear,
      p_lease_numbers: args.leaseNumbers ?? null,
    }),

  requestAnalysisJobCancel: (jobId: string) =>
    supabase.rpc("request_analysis_job_cancel", { p_job_id: jobId }),

  analysisQueueCounts: () => supabase.rpc("analysis_queue_counts"),

  reviewAnalysisResult: (args: {
    resultId: string;
    verificationStatus: string;
    verificationNotes: string | null;
  }) =>
    supabase.rpc("review_analysis_result", {
      p_result_id: args.resultId,
      p_verification_status: args.verificationStatus,
      p_verification_notes: args.verificationNotes,
    }),

  grantLeaseAccess: (leaseNumber: string, email: string, notes?: string) =>
    supabase.rpc("grant_lease_access", {
      p_lease_number: leaseNumber,
      p_email: email,
      p_notes: notes ?? null,
    }),

  revokeLeaseAccess: (leaseNumber: string, email: string) =>
    supabase.rpc("revoke_lease_access", {
      p_lease_number: leaseNumber,
      p_email: email,
    }),

  // ── Entity writes: ALWAYS by id (20260925010000). The name-keyed RPCs
  // silently picked an arbitrary row when two entities shared a name; they
  // now refuse ambiguous names, and nothing here calls them.

  assignEntityToLease: (args: {
    leaseNumber: string;
    entityId: string;
    ownershipPct?: number | null;
    isPrimary?: boolean;
    notes?: string | null;
  }) =>
    supabase.rpc("assign_entity_to_lease_by_id", {
      p_lease_number: args.leaseNumber,
      p_entity_id: args.entityId,
      p_ownership_percentage: args.ownershipPct ?? null,
      p_is_primary: args.isPrimary ?? false,
      p_notes: args.notes ?? null,
    }),

  /** Link several unowned leases to one entity, all or nothing. */
  assignEntityToLeases: (args: { entityId: string; leaseNumbers: string[]; notes?: string | null }) =>
    supabase.rpc("assign_entity_to_leases_by_id", {
      p_entity_id: args.entityId,
      p_lease_numbers: args.leaseNumbers,
      p_notes: args.notes ?? null,
    }),

  unassignEntityFromLease: (leaseNumber: string, entityId: string) =>
    supabase.rpc("unassign_entity_from_lease_by_id", {
      p_lease_number: leaseNumber,
      p_entity_id: entityId,
    }),

  // Flag-only change on an EXISTING membership; demotes any other primary in
  // the same call. userId null leaves the entity with no primary contact.
  setPrimaryContact: (entityId: string, userId: string | null) =>
    supabase.rpc("set_primary_contact_by_id", {
      p_entity_id: entityId,
      p_user_id: userId,
    }),

  // Program enrollment (entity-level: a lease is in the program because its
  // OWNER is enrolled — assignment alone never enrolls).
  enrollEntity: (entityId: string, onDate?: string, reason?: string | null) =>
    supabase.rpc("enroll_entity_by_id", {
      p_entity_id: entityId,
      p_on: onDate ?? new Date().toISOString().slice(0, 10),
      p_reason: reason ?? null,
    }),

  withdrawEntity: (entityId: string, onDate?: string, reason?: string | null) =>
    supabase.rpc("withdraw_entity_by_id", {
      p_entity_id: entityId,
      p_on: onDate ?? new Date().toISOString().slice(0, 10),
      p_reason: reason ?? null,
    }),

  /** Staff: transfer the lease named in a client's notice and close it. */
  completeLeaseTransferRequest: (args: {
    requestId: string;
    newEntityId: string;
    effectiveDate?: string | null;
    keepAccessUserIds?: string[] | null;
  }) =>
    supabase.rpc("complete_lease_transfer_request", {
      p_request_id: args.requestId,
      p_new_entity_id: args.newEntityId,
      p_effective_date: args.effectiveDate ?? null,
      p_keep_access_user_ids: args.keepAccessUserIds === undefined ? [] : args.keepAccessUserIds,
    }),

  /** Staff review queue: leases with no owner record. */
  listUnlinkedLeases: () => supabase.rpc("list_unlinked_leases"),

  /** Staff: every lease one user reaches, and how (entity / owner / direct). */
  listUserLeaseAccess: (userId: string) =>
    supabase.rpc("list_user_lease_access", { p_user_id: userId }),

  // Annual season reset (migration 48): every lease's analysis_state back to
  // 'Not Ready', then sampling_status recomputed from current-year samples
  // (which re-promotes >=2-sample leases to Ready after Jul 1). Internal-only
  // (gated in SQL); returns a before/after jsonb summary.
  annualLeaseStatusReset: () =>
    supabase.rpc("annual_lease_status_reset" as never),

  // keepAccessUserIds: users whose direct lease grant survives the sale.
  // [] (default) revokes every direct grant; null keeps them all.
  transferLeaseOwnership: (args: {
    leaseNumber: string;
    newEntityId: string;
    effectiveDate: string;
    notes?: string | null;
    keepAccessUserIds?: string[] | null;
  }) =>
    supabase.rpc("transfer_lease_ownership", {
      p_lease_number: args.leaseNumber,
      p_new_entity_id: args.newEntityId,
      p_effective_date: args.effectiveDate,
      p_notes: args.notes?.trim() || null,
      p_keep_access_user_ids: args.keepAccessUserIds === undefined ? [] : args.keepAccessUserIds,
    }),

  transferLeaseOwnershipBatch: (args: {
    leaseNumbers: string[];
    newEntityId: string;
    effectiveDate: string;
    notes?: string | null;
    keepAccessUserIds?: string[] | null;
  }) =>
    supabase.rpc("transfer_lease_ownership_batch", {
      p_lease_numbers: args.leaseNumbers,
      p_new_entity_id: args.newEntityId,
      p_effective_date: args.effectiveDate,
      p_notes: args.notes?.trim() || null,
      p_keep_access_user_ids: args.keepAccessUserIds === undefined ? [] : args.keepAccessUserIds,
    }),

  // Duplicate entities (migration 66). Internal-only on the server.
  mergeEntitiesPreview: (keepId: string, dropId: string) =>
    supabase.rpc("merge_entities_preview", { p_keep: keepId, p_drop: dropId }),

  mergeEntities: (keepId: string, dropId: string, reason?: string | null) =>
    supabase.rpc("merge_entities", { p_keep: keepId, p_drop: dropId, p_reason: reason?.trim() || null }),

  // Lease import (migration 64). Both are internal-only on the server.
  leaseImportPreview: (geometry: Record<string, unknown>) =>
    supabase.rpc("lease_import_preview", { p_geometry: geometry }),

  createLeaseFromGeojson: (args: {
    leaseNumber: string;
    geometry: Record<string, unknown>;
    attributes: Record<string, string | boolean | null>;
    entityId?: string | null;
    enrollEntity?: boolean;
    allowOverlap?: boolean;
  }) =>
    supabase.rpc("create_lease_from_geojson", {
      p_lease_number: args.leaseNumber.trim(),
      p_geometry: args.geometry,
      p_attributes: args.attributes,
      p_entity_name: null,
      p_entity_id: args.entityId ?? null,
      p_enroll_entity: args.enrollEntity ?? false,
      p_allow_overlap: args.allowOverlap ?? false,
    }),

  // Re-adding an existing member only changes the values supplied here; it
  // no longer resets their ownership %, role or primary flag.
  addUserToEntity: (args: {
    userId: string;
    entityId: string;
    ownershipPct?: number | null;
    isPrimaryContact?: boolean;
    role?: string | null;
  }) =>
    supabase.rpc("add_user_to_entity_by_id", {
      p_user_id: args.userId,
      p_entity_id: args.entityId,
      p_ownership_percentage: args.ownershipPct ?? null,
      p_is_primary_contact: args.isPrimaryContact ?? false,
      p_role: args.role ?? null,
    }),

  removeUserFromEntity: (userId: string, entityId: string) =>
    supabase.rpc("remove_user_from_entity_by_id", {
      p_user_id: userId,
      p_entity_id: entityId,
    }),

  listLeaseAccess: (leaseNumber: string) =>
    supabase.rpc("list_lease_access", { p_lease_number: leaseNumber }),

  // entity_ownership: parent = the entity being owned, child = the owner.
  addEntityOwnership: (args: {
    parentEntityId: string;
    childEntityId: string;
    ownershipPercentage?: number | null;
    role?: string | null;
    notes?: string | null;
  }) =>
    supabase.rpc("add_entity_ownership_by_id", {
      p_parent_entity_id:     args.parentEntityId,
      p_child_entity_id:      args.childEntityId,
      p_ownership_percentage: args.ownershipPercentage ?? null,
      p_role:                 args.role ?? null,
      p_notes:                args.notes ?? null,
    }),

  removeEntityOwnership: (parentEntityId: string, childEntityId: string) =>
    supabase.rpc("remove_entity_ownership_by_id", {
      p_parent_entity_id: parentEntityId,
      p_child_entity_id:  childEntityId,
    }),

  // ─── W-9 (20260819000000) ─────────────────────────────────────────────
  // Only the client/staff-callable RPCs. Submitting, accepting, rejecting and
  // the one-time signing code all run service-role inside the `w9-submit`
  // Edge Function — there is deliberately no wrapper for them here.

  /** Every entity the caller can see, whether they owe a W-9 for it, and what
   *  the last attempt did. Definer, because a client cannot read the owner
   *  entities behind their own partnership under entity_ownership RLS. */
  w9MyGraph: () => supabase.rpc("w9_my_graph"),

  /** Prefill for one entity's form. Returns tin presence only, never digits. */
  w9FormPrefill: (entityId: string) =>
    supabase.rpc("w9_form_prefill", { p_entity_id: entityId }),

  /** IRS electronic-submission evidence: log the occasions of access that lead
   *  to a submission. Fire-and-forget from the UI. */
  w9LogAccess: (args: { entityId?: string | null; submissionId?: string | null; event: string }) =>
    supabase.rpc("w9_log_access", {
      p_entity_id:     args.entityId     ?? null,
      p_submission_id: args.submissionId ?? null,
      p_event:         args.event,
    }),

  /** Batch status for a page of entities, for badges in a list. */
  w9StatusForEntities: (entityIds: string[]) =>
    supabase.rpc("w9_status_for_entities", { p_entity_ids: entityIds }),

  /** Staff review: the on-file PII to compare a submission against. */
  w9PiiSnapshotForReview: (entityId: string) =>
    supabase.rpc("w9_pii_snapshot_for_review", { p_entity_id: entityId }),

  /** Staff: ask a leaseholder to file again. Stays open until one is ACCEPTED. */
  w9RequestRefile: (entityId: string, reason: string) =>
    supabase.rpc("w9_request_refile", { p_entity_id: entityId, p_reason: reason }),

  /** Staff: close a "not a U.S. person" referral (that filer needs a W-8). */
  w9ResolveNonUs: (referralId: string, notes: string | null) =>
    supabase.rpc("w9_resolve_non_us", { p_referral_id: referralId, p_notes: notes }),

  upsertPii: (args: {
    entityId: string;                  // required
    taxIdNew?: string | null;          // pass new plaintext; null = preserve existing
    clearTaxId?: boolean;
    phoneNumber?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    county?: string | null;
  }) =>
    supabase.rpc("upsert_pii", {
      p_entity_id:     args.entityId,
      p_tax_id_new:    args.taxIdNew     ?? null,
      p_clear_tax_id:  args.clearTaxId   ?? false,
      p_phone_number:  args.phoneNumber  ?? null,
      p_address_1:     args.address1     ?? null,
      p_address_2:     args.address2     ?? null,
      p_city:          args.city         ?? null,
      p_state:         args.state        ?? null,
      p_zip:           args.zip          ?? null,
      p_county:        args.county       ?? null,
    }),
};
